import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase, Venue } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import BottomNav from '../components/BottomNav';
import LocationMapPicker from '../components/LocationMapPicker';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/ui/Button';
import { ArrowLeft, Calendar, MapPin, FileText, Map, Image as ImageIcon } from 'lucide-react';

export default function EditEvent() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { currentUser } = useEmberStore();
  
  const [venueName, setVenueName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxAttendees, setMaxAttendees] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Photo editing
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  
  // Event visibility
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  async function loadEvent() {
    if (!eventId) return;
    
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) throw error;
      
      if (data) {
        // Check if user is the creator
        if (data.creator_id !== currentUser?.id) {
          setError('You can only edit your own events');
          setTimeout(() => navigate('/profile'), 2000);
          return;
        }

        // Pre-fill form
        setTitle(data.title);
        setDescription(data.description || '');
        setVenueName(data.venue_name || '');
        setMaxAttendees(data.max_attendees || '');
        setExistingPhotoUrl(data.photo_url || null);
        setIsPublic(data.is_public !== false); // Default to true if undefined
        
        // Load location if available
        if (data.latitude && data.longitude) {
          setSelectedLocation({
            latitude: data.latitude,
            longitude: data.longitude,
            address: `${data.latitude.toFixed(5)}, ${data.longitude.toFixed(5)}`
          });
        }
        
        // Format dates
        const startDateTime = new Date(data.start_datetime);
        setStartDate(startDateTime.toISOString().split('T')[0]);
        setStartTime(startDateTime.toTimeString().slice(0, 5));
        
        if (data.end_datetime) {
          const endDateTime = new Date(data.end_datetime);
          setEndDate(endDateTime.toISOString().split('T')[0]);
          setEndTime(endDateTime.toTimeString().slice(0, 5));
        }
      }
    } catch (err) {
      console.error('Error loading event:', err);
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    if (!title.trim() || !description.trim() || !startDate || !startTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!venueName.trim()) {
      toast.error('Please enter a venue/location name');
      return;
    }

    if (!selectedLocation) {
      toast.error('Please set the event location on the map');
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      const startDateTime = new Date(`${startDate}T${startTime}`).toISOString();
      const endDateTime = endDate && endTime ? new Date(`${endDate}T${endTime}`).toISOString() : null;
      // Upload new photo if provided
      let newPhotoUrl: string | undefined = undefined;
      if (photoFile) {
        const filePath = `${currentUser?.id}/${Date.now()}-${photoFile.name}`;
        const { data: up, error: upErr } = await supabase.storage
          .from('event-photos')
          .upload(filePath, photoFile, { upsert: false });
        if (!upErr && up) {
          const { data: pub } = supabase.storage
            .from('event-photos')
            .getPublicUrl(up.path);
          newPhotoUrl = pub?.publicUrl ?? null as any;
        } else {
          toast.error('Image upload failed; keeping previous image');
        }
      }

      const updates: any = {
        title: title.trim(),
        description: description.trim(),
        venue_name: venueName.trim(),
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        max_attendees: maxAttendees || null,
        is_public: isPublic,
        updated_at: new Date().toISOString(),
      };
      if (typeof newPhotoUrl !== 'undefined') {
        updates.photo_url = newPhotoUrl;
      }

      const { error } = await supabase
        .from('events')
        .update(updates)
        .eq('id', eventId);

      if (error) throw error;
      
      navigate('/profile');
    } catch (err) {
      console.error('Error updating event:', err);
      setError('Failed to update event');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner fullPage text="Loading event..." />;
  }

  if (error && error.includes('only edit your own')) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ color: 'var(--color-danger)' }}>{error}</p>
        <p style={{ color: '#999' }}>Redirecting to profile...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #1f1f1f',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        position: 'sticky',
        top: 0,
        backgroundColor: '#0b0b0b',
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/profile')}
          style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
        >
          <ArrowLeft size={24} />
        </button>
        <Calendar size={24} color="var(--color-accent)" />
        <h1 style={{ fontSize: '1.5rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          Edit Event
        </h1>
      </header>

      {/* Form */}
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: 'rgba(228, 87, 77, 0.1)',
            border: '1px solid var(--color-danger)',
            borderRadius: '8px',
            color: 'var(--color-danger)',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Event Photo */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Event Photo (Optional)
            </label>

            {/* Current or Preview */}
            {(photoPreview || existingPhotoUrl) && (
              <div style={{ marginBottom: '0.75rem' }}>
                <img
                  src={photoPreview || existingPhotoUrl || ''}
                  alt="Event"
                  style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', backgroundColor: '#1f1f1f' }}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label htmlFor="edit-event-photo" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: 'var(--color-card)'
              }}>
                <ImageIcon size={24} color="var(--color-accent)" />
                <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose New Photo</span>
              </label>
              {existingPhotoUrl && (
                <button type="button" onClick={() => { setExistingPhotoUrl(null); setPhotoPreview(null); setPhotoFile(null); }} style={{
                  padding: '1rem', border: '1px solid #444', borderRadius: '8px', background: 'transparent', color: '#ccc'
                }}>Remove Current Photo</button>
              )}
            </div>

            <input id="edit-event-photo" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setPhotoFile(file);
              if (file) {
                const reader = new FileReader();
                reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
                reader.readAsDataURL(file);
              } else {
                setPhotoPreview(null);
              }
            }} />
          </div>

          
          {/* Venue Name */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              <MapPin size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Venue/Location Name *
            </label>
            <input
              type="text"
              value={venueName}
              onChange={(e) => setVenueName(e.target.value)}
              placeholder="e.g., The Pipe Lounge, Starbucks"
              style={{ width: '100%' }}
              required
            />
          </div>

          {/* Location Editor */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Event Location *
            </label>
            
            {selectedLocation ? (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(63, 160, 201, 0.1)',
                border: '2px solid var(--color-secondary)',
                borderRadius: '8px'
              }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: '600', color: 'var(--color-secondary)', marginBottom: '0.25rem' }}>
                    📍 Location Set
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>
                    {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMapPicker(true)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-secondary)',
                    borderRadius: '6px',
                    color: 'var(--color-secondary)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Map size={16} />
                  Edit Location
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowMapPicker(true)}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: 'var(--color-card)',
                  border: '2px dashed var(--color-accent)',
                  borderRadius: '8px',
                  color: 'var(--color-accent)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <Map size={20} />
                Set Location on Map
              </button>
            )}
          </div>

          {/* Event Title */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Event Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Pipe Smoking Book Club, Monthly Meetup"
              style={{ width: '100%' }}
              required
            />
          </div>

          {/* Event Description */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              <FileText size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this event is about, what to expect, what to bring..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.875rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              required
            />
          </div>

          {/* Start Date & Time */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%' }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Start Time *
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                style={{ width: '100%' }}
                required
              />
            </div>
          </div>

          {/* End Date & Time (Optional) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                End Date (Optional)
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%' }}
                min={startDate || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                End Time (Optional)
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Max Attendees */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Max Attendees (Optional)
            </label>
            <input
              type="number"
              value={maxAttendees}
              onChange={(e) => setMaxAttendees(e.target.value ? parseInt(e.target.value) : '')}
              placeholder="Leave empty for unlimited"
              min="1"
              style={{ width: '100%' }}
            />
          </div>

          {/* Event Visibility Toggle */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Event Visibility
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              backgroundColor: isPublic ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
              border: `2px solid ${isPublic ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: '8px'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                  {isPublic ? '🌍 Public Event' : '🔒 Private Event'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#999' }}>
                  {isPublic 
                    ? 'Visible to all users. Nearby users will be notified.' 
                    : 'Only visible to invited users. No proximity notifications sent.'
                  }
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                style={{
                  width: '50px',
                  height: '28px',
                  backgroundColor: isPublic ? 'var(--color-accent)' : '#666',
                  border: 'none',
                  borderRadius: '14px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginLeft: '1rem',
                  flexShrink: 0
                }}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: isPublic ? '24px' : '2px',
                  transition: 'left 0.2s ease'
                }} />
              </button>
            </div>
            
            {isPublic && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: 'rgba(196, 255, 0, 0.05)',
                border: '1px solid rgba(196, 255, 0, 0.2)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#cfcfcf'
              }}>
                <strong style={{ color: 'var(--color-accent)' }}>📢 Public Event Notifications</strong>
                <br />
                • Users within their notification radius will be alerted
                <br />
                • Event will appear on the map and discover page
                <br />
                • Anyone can view and respond to this event
              </div>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={saving}
            fullWidth
          >
            Save Changes
          </Button>
        </form>
      </div>

      <BottomNav />

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '800px',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--color-border)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Edit Event Location</h3>
              <button
                onClick={() => setShowMapPicker(false)}
                style={{
                  padding: '0.5rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: '1.5rem'
                }}
              >
                ×
              </button>
            </div>

            {/* Map Content */}
            <div style={{ flex: 1, padding: '1rem' }}>
              <LocationMapPicker
                initialLocation={selectedLocation ? {
                  lat: selectedLocation.latitude,
                  lng: selectedLocation.longitude
                } : undefined}
                onLocationSelected={(location) => {
                  setSelectedLocation({
                    latitude: location.latitude,
                    longitude: location.longitude,
                    address: location.address
                  });
                  setShowMapPicker(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
